import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import { useMobile } from '../hooks/useMobile';

const token = () => localStorage.getItem('token');
const API = process.env.REACT_APP_API_URL || 'https://brokerconnect.co.uk/api';
const PUBLIC_BOOKING_BASE = 'https://brokerconnect.co.uk/book';

export default function Settings() {
  const location = typeof window !== 'undefined' ? window.location : { search: '' };
  const require2fa = new URLSearchParams(location.search).get('require2fa') === '1';
  const calendarParam = new URLSearchParams(location.search).get('calendar'); // 'connected' | 'error' — set by GET /api/calendar/callback's redirect
  const calendarErrorReason = new URLSearchParams(location.search).get('reason');
  const initialTab = new URLSearchParams(location.search).get('tab');
  const navigate = useNavigate();
  const mobile    = useMobile();
  const [user, setUser] = useState(null);
  const [activeTab, setActiveTab] = useState(initialTab || 'account');

  // Password
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword,     setNewPassword]      = useState('');
  const [confirmPassword, setConfirmPassword]  = useState('');
  const [pwLoading,  setPwLoading]  = useState(false);
  const [pwError,    setPwError]    = useState('');
  const [pwSuccess,  setPwSuccess]  = useState('');

  // Calendar sync (Part 1, CALENDAR-WHATSAPP-SCOPE.md) — each adviser connects their
  // own Google/Outlook calendar via Aurinko. Section only renders once the company has
  // nav_modules.scheduling on; a 403 from /api/calendar/status just hides it, same
  // 'hide/explain rather than show a button that would 403' pattern used elsewhere.
  const [calendarAvailable, setCalendarAvailable] = useState(false);
  const [calendarConnection, setCalendarConnection] = useState(null); // { provider, aurinko_account_email, status, connected_at } | null
  const [calendarConnecting, setCalendarConnecting] = useState(false);
  const [calendarError,     setCalendarError]     = useState('');
  const calendarBannerMsg =
    calendarParam === 'connected' ? 'Calendar connected.'
    : calendarParam === 'error' ? `Couldn't connect your calendar (${calendarErrorReason || 'unknown error'}). Please try again.`
    : '';
  // Booking hours (Part 3, CALENDAR-WHATSAPP-SCOPE.md) — each adviser's own Aurinko
  // booking profile (work hours/duration). Self-service: only the adviser who owns
  // the calendar can see/edit this, never set on their behalf (Tim's decision, 15 Aug).
  const [bookingProfile, setBookingProfile] = useState(null);
  const [bookingProfileLoading, setBookingProfileLoading] = useState(false);
  const [bookingProfileError, setBookingProfileError] = useState('');
  const [bookingProfileSuccess, setBookingProfileSuccess] = useState('');
  const [bpDuration, setBpDuration] = useState(30);
  const [bpAvailabilityStep, setBpAvailabilityStep] = useState(15); // interval between offered slot times - distinct from duration, added 19 Aug 2026
  const [bpDaySchedules, setBpDaySchedules] = useState({}); // { monday: {enabled, start, end}, ... }

  // Personal booking link (item A, 19 Aug 2026) - self-service, one per adviser.
  const [personalLink, setPersonalLink] = useState(null);
  const [personalLinkLoading, setPersonalLinkLoading] = useState(false);
  const [personalLinkError, setPersonalLinkError] = useState('');

  // Company-wide booking page config (folded in from the old standalone
  // BookingPageSettings.jsx / '/booking-setup' page, 19 Aug 2026) - owner only.
  const [companyPage, setCompanyPage] = useState(null);
  const [companyAdvisers, setCompanyAdvisers] = useState([]);
  const [eligibleAdvisers, setEligibleAdvisers] = useState([]);
  const [companyPageLoading, setCompanyPageLoading] = useState(false);
  const [companyPageError, setCompanyPageError] = useState('');
  const [companyPageSuccess, setCompanyPageSuccess] = useState('');
  const [companySlugInput, setCompanySlugInput] = useState('');
  const [companyModeInput, setCompanyModeInput] = useState('named');
  const [companyAddUserId, setCompanyAddUserId] = useState('');
  const [companySaving, setCompanySaving] = useState(false);

  // Intake pool config (item B/E, 19 Aug 2026) - owner picks which advisers are
  // eligible to pick up an unallocated Back Book client's booking, plus the pool's
  // own duration/interval (rotation mode has no single adviser 'owner' of that).
  const [intakePage, setIntakePage] = useState(null);
  const [intakeAdvisers, setIntakeAdvisers] = useState([]);
  const [intakePageLoading, setIntakePageLoading] = useState(false);
  const [intakePageError, setIntakePageError] = useState('');
  const [intakePageSuccess, setIntakePageSuccess] = useState('');
  const [intakeSlugInput, setIntakeSlugInput] = useState('');
  const [intakeDurationInput, setIntakeDurationInput] = useState(30);
  const [intakeStepInput, setIntakeStepInput] = useState(15);
  const [intakeAddUserId, setIntakeAddUserId] = useState('');
  const [intakeSaving, setIntakeSaving] = useState(false);


  // Two-Factor Authentication
  const [twoFaStatus,     setTwoFaStatus]     = useState(null); // { enabled, hasPhone, setupDate }
  const [twoFaStep,       setTwoFaStep]       = useState('idle'); // idle | qr | backupCodes | disable
  // Auto-start 2FA setup if company mandates it and user hasn't done it
  const [require2FABanner, setRequire2FABanner] = useState(require2fa);
  const [twoFaSecret,     setTwoFaSecret]     = useState(null); // { secret, qrCode, manualEntry }
  const [twoFaTotpInput,  setTwoFaTotpInput]  = useState('');
  const [twoFaPhoneInput, setTwoFaPhoneInput] = useState('');
  const [twoFaBackupCodes,setTwoFaBackupCodes]= useState([]);
  const [twoFaDisablePw,  setTwoFaDisablePw]  = useState('');
  const [twoFaLoading,    setTwoFaLoading]    = useState(false);
  const [twoFaError,      setTwoFaError]      = useState('');
  const [twoFaSuccess,    setTwoFaSuccess]    = useState('');

  // My Profile
  const [myBio,           setMyBio]           = useState('');
  const [myPhotoUrl,      setMyPhotoUrl]       = useState('');
  const [profileSaving,   setProfileSaving]    = useState(false);
  const [profileError,    setProfileError]     = useState('');
  const [profileSuccess,  setProfileSuccess]   = useState('');
  const profilePhotoRef = useRef();

  // Introducer entity (individual vs company)
  const [entityType,      setEntityType]      = useState('individual');
  const [entityCompanyName, setEntityCompanyName] = useState('');
  const [entityAddress,   setEntityAddress]   = useState('');
  const [entitySaving,    setEntitySaving]    = useState(false);
  const [entityError,     setEntityError]     = useState('');
  const [entitySuccess,   setEntitySuccess]   = useState('');

  // Company Profile
  const [companyBio,      setCompanyBio]      = useState('');
  const [companyLogoUrl,  setCompanyLogoUrl]  = useState('');
  const [officePhotoUrl,  setOfficePhotoUrl]  = useState('');
  const [leadRouting,     setLeadRouting]     = useState('pool');
  const [poolStrategy,      setPoolStrategy]      = useState('first_to_claim');
  const [brokerUserCount, setBrokerUserCount] = useState(null); // null = not loaded yet; hides the pool option until known
  const [claimWindowMins,   setClaimWindowMins]   = useState(30);
  const [claimTimeoutAction,setClaimTimeoutAction] = useState('admin');
  const [compProfSaving,  setCompProfSaving]  = useState(false);
  const [compProfError,   setCompProfError]   = useState('');
  const [compProfSuccess, setCompProfSuccess] = useState('');
  const logoRef       = useRef();
  const officePhotoRef = useRef();

  // Commission settings
  const [networkFeePct, setNetworkFeePct] = useState('');
  const [compAlloc,     setCompAlloc]     = useState('');
  const [mgrAlloc,      setMgrAlloc]      = useState('');
  const [brkAlloc,      setBrkAlloc]      = useState('');
  const [commSaving,    setCommSaving]    = useState(false);
  const [commError,     setCommError]     = useState('');
  const [commSuccess,   setCommSuccess]   = useState('');

  // Preferences (DIP + nag)
  const [compSettings, setCompSettings] = useState(null);
  const [widgetEnabled, setWidgetEnabled] = useState(false);
  const [dipExpiry,    setDipExpiry]     = useState(30);
  const [nagDays,      setNagDays]       = useState([7, 14, 21]);
  const [nagInput,     setNagInput]      = useState('');
  const [settLoading,  setSettLoading]   = useState(false);
  const [settError,    setSettError]     = useState('');
  const [settSuccess,  setSettSuccess]   = useState('');

  // Which modules this company has switched on (14 Aug 2026 — Settings now adapts the
  // same way Sidebar/Dashboard already do; untouched for a normal all-modules-on account).
  const [navModules, setNavModules] = useState({
    leads: true, deals: true, introducers: true, backbook: false,
    clients: true, analytics: true, announcements: true,
  });

  // Back Book preferences: renewal alert trigger months + monthly director digest
  // opt-in. Same chip-list pattern as the DIP nag-days above, backed by the new
  // /api/backbook/alert-schedule and /api/backbook/digest-preference endpoints.
  const [alertMonths,   setAlertMonths]   = useState([8, 6, 3]);
  const [alertInput,    setAlertInput]    = useState('');
  const [digestEnabled, setDigestEnabled] = useState(false);
  const [bbPrefLoading, setBbPrefLoading] = useState(false);
  const [bbPrefError,   setBbPrefError]   = useState('');
  const [bbPrefSuccess, setBbPrefSuccess] = useState('');

  const isBrokerOwner = user && (
    user.role === 'owner' ||
    (user.role === 'broker' && (user.brokerLevel === 'owner' || user.broker_level === 'owner'))
  );
  const isBrokerAdmin = user && ['broker_owner','broker_admin','broker_manager'].includes(user.role);

  // ── Bootstrap user from JWT ──────────────────────────────────────────────
  useEffect(() => {
    const tok = token();
    if (!tok) { navigate('/login'); return; }
    try {
      const payload = JSON.parse(atob(tok.split('.')[1]));
      setUser(payload);
    } catch { navigate('/login'); }
  }, [navigate]);

  // ── Load own profile ─────────────────────────────────────────────────────
  useEffect(() => {
    if (!user) return;
    fetch(`${API}/profile/me`, { headers: { Authorization: `Bearer ${token()}` } })
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (d) {
          setMyBio(d.user.bio || '');
          setMyPhotoUrl(d.user.profile_photo_url || '');
          setEntityType(d.user.entity_type || 'individual');
          setEntityCompanyName(d.user.company_name || '');
          setEntityAddress(d.user.company_address || '');
        }
      })
      .catch(() => {});
  }, [user]);

  // ── Load 2FA status ───────────────────────────────────────────────────────
  const loadTwoFaStatus = () => {
    fetch(`${API}/2fa/status`, { headers: { Authorization: `Bearer ${token()}` } })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setTwoFaStatus(d); })
      .catch(() => {});
  };
  useEffect(() => { if (user) loadTwoFaStatus(); }, [user]);

  // ── Load calendar connection status ──────────────────────────────────────
  const loadCalendarStatus = () => {
    fetch(`${API}/calendar/status`, { headers: { Authorization: `Bearer ${token()}` } })
      .then(r => {
        if (r.status === 403) { setCalendarAvailable(false); return null; }
        setCalendarAvailable(true);
        return r.ok ? r.json() : null;
      })
      .then(d => { if (d) setCalendarConnection(d.connection); })
      .catch(() => {});
  };
  useEffect(() => { if (user) loadCalendarStatus(); }, [user]);

  const handleConnectCalendar = async (provider) => {
    setCalendarConnecting(true);
    setCalendarError('');
    try {
      const res = await fetch(`${API}/calendar/connect?provider=${provider}`, { headers: { Authorization: `Bearer ${token()}` } });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.authorizeUrl) {
        window.location.href = data.authorizeUrl; // full-page redirect to the provider's consent screen
      } else {
        setCalendarError(data.error || 'Failed to start calendar connection');
        setCalendarConnecting(false);
      }
    } catch {
      setCalendarError('Failed to start calendar connection');
      setCalendarConnecting(false);
    }
  };

  const handleDisconnectCalendar = async () => {
    if (!window.confirm('Disconnect your calendar? Booking reminders and the team calendar view will stop reflecting your real availability until you reconnect.')) return;
    try {
      const res = await fetch(`${API}/calendar/disconnect`, { method: 'DELETE', headers: { Authorization: `Bearer ${token()}` } });
      if (res.ok) { setCalendarConnection(null); loadCalendarStatus(); }
      else setCalendarError('Failed to disconnect calendar');
    } catch {
      setCalendarError('Failed to disconnect calendar');
    }
  };
  // ── Booking hours (Part 3) — self-service, own calendar only ────────────
  const loadBookingProfile = () => {
    setBookingProfileLoading(true);
    setBookingProfileError('');
    fetch(`${API}/booking-profile/me`, { headers: { Authorization: `Bearer ${token()}` } })
      .then(r => { if (!r.ok) throw new Error('load failed'); return r.json(); })
      .then(d => {
        // Previously failed silently here (no error, no form) whenever the backend
        // couldn't produce a profile — e.g. the real 18 Aug bug where Aurinko
        // rejected a display name containing a space. Surface it now instead of
        // rendering an empty section with no feedback.
        if (!d || !d.profile) {
          setBookingProfileError(
            d && d.hasCalendar === false
              ? 'Connect your calendar above first.'
              : 'Failed to load your booking hours — please try refreshing.'
          );
          return;
        }
        setBookingProfile(d.profile);
        setBpDuration(d.profile.durationMinutes || 30);
        setBpAvailabilityStep(d.profile.availabilityStep || 15);
        const days = {};
        ['monday','tuesday','wednesday','thursday','friday','saturday','sunday'].forEach(day => {
          const sched = (d.profile.workHours?.daySchedules || []).find(s => s.dayOfWeek === day);
          const interval = sched?.workingIntervals?.[0];
          days[day] = { enabled: !!interval, start: (interval?.start || '09:00:00').slice(0,5), end: (interval?.end || '17:00:00').slice(0,5) };
        });
        setBpDaySchedules(days);
      })
      .catch(() => setBookingProfileError('Failed to load your booking hours'))
      .finally(() => setBookingProfileLoading(false));
  };
  useEffect(() => { if (calendarConnection) loadBookingProfile(); }, [calendarConnection]);

  const handleSaveBookingHours = async (e) => {
    e.preventDefault();
    setBookingProfileLoading(true); setBookingProfileError(''); setBookingProfileSuccess('');
    try {
      const daySchedules = Object.entries(bpDaySchedules)
        .filter(([, v]) => v.enabled)
        .map(([dayOfWeek, v]) => ({ dayOfWeek, workingIntervals: [{ start: `${v.start}:00`, end: `${v.end}:00` }] }));
      const res = await fetch(`${API}/booking-profile/me`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
        body: JSON.stringify({ durationMinutes: Number(bpDuration), availabilityStep: Number(bpAvailabilityStep), workHours: { timezone: 'Europe/London', daySchedules } }),
      });
      if (res.ok) { setBookingProfileSuccess('Booking hours saved.'); loadBookingProfile(); }
      else setBookingProfileError('Failed to save booking hours');
    } catch { setBookingProfileError('Failed to save booking hours'); }
    setBookingProfileLoading(false);
  };

  // ── Personal booking link (item A) — self-service, one per adviser ────────
  const loadPersonalLink = () => {
    fetch(`${API}/booking-profile/me/personal-link`, { headers: { Authorization: `Bearer ${token()}` } })
      .then(r => r.ok ? r.json() : { page: null })
      .then(d => setPersonalLink(d.page))
      .catch(() => {});
  };
  useEffect(() => { if (calendarConnection) loadPersonalLink(); }, [calendarConnection]);

  const handleCreatePersonalLink = async () => {
    setPersonalLinkLoading(true); setPersonalLinkError('');
    try {
      const res = await fetch(`${API}/booking-profile/me/personal-link`, {
        method: 'POST', headers: { Authorization: `Bearer ${token()}` },
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) setPersonalLink(data.page);
      else setPersonalLinkError(data.error || 'Failed to create your personal booking link');
    } catch { setPersonalLinkError('Failed to create your personal booking link'); }
    setPersonalLinkLoading(false);
  };

  // ── Company-wide booking page (owner only) — folded in from the old
  // standalone BookingPageSettings.jsx / '/booking-setup' page ───────────────
  const loadCompanyPage = () => {
    if (!isBrokerOwner) return;
    setCompanyPageLoading(true);
    Promise.all([
      fetch(`${API}/broker/booking-page?type=general`, { headers: { Authorization: `Bearer ${token()}` } }).then(r => r.ok ? r.json() : { page: null, advisers: [] }),
      fetch(`${API}/broker/booking-page/eligible-advisers`, { headers: { Authorization: `Bearer ${token()}` } }).then(r => r.ok ? r.json() : []).catch(() => []),
    ]).then(([bp, team]) => {
      setCompanyPage(bp.page);
      setCompanyAdvisers(bp.advisers || []);
      setEligibleAdvisers(Array.isArray(team) ? team : []);
      if (bp.page) { setCompanySlugInput(bp.page.slug); setCompanyModeInput(bp.page.adviser_selection_mode); }
    }).catch(() => setCompanyPageError('Failed to load booking page settings')).finally(() => setCompanyPageLoading(false));
  };
  useEffect(() => { if (user && isBrokerOwner && navModules.scheduling) loadCompanyPage(); }, [user, isBrokerOwner, navModules.scheduling]);

  const handleCreateCompanyPage = async (e) => {
    e.preventDefault();
    setCompanySaving(true); setCompanyPageError(''); setCompanyPageSuccess('');
    try {
      const res = await fetch(`${API}/broker/booking-page`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
        body: JSON.stringify({ slug: companySlugInput.trim().toLowerCase(), adviserSelectionMode: companyModeInput, pageType: 'general' }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) { setCompanyPageSuccess('Booking page created.'); loadCompanyPage(); }
      else setCompanyPageError(data.error || 'Failed to create booking page');
    } catch { setCompanyPageError('Failed to create booking page'); }
    setCompanySaving(false);
  };

  const handleAddCompanyAdviser = async () => {
    if (!companyAddUserId || !companyPage) return;
    setCompanySaving(true); setCompanyPageError(''); setCompanyPageSuccess('');
    try {
      const res = await fetch(`${API}/broker/booking-page/${companyPage.id}/advisers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
        body: JSON.stringify({ userId: companyAddUserId }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) { setCompanyAddUserId(''); loadCompanyPage(); }
      else setCompanyPageError(data.error || 'Failed to add adviser');
    } catch { setCompanyPageError('Failed to add adviser'); }
    setCompanySaving(false);
  };

  const handleRemoveCompanyAdviser = async (adviserRowId) => {
    if (!window.confirm('Remove this adviser from the booking page?')) return;
    try {
      const res = await fetch(`${API}/broker/booking-page/${companyPage.id}/advisers/${adviserRowId}`, {
        method: 'DELETE', headers: { Authorization: `Bearer ${token()}` },
      });
      if (res.ok) loadCompanyPage(); else setCompanyPageError('Failed to remove adviser');
    } catch { setCompanyPageError('Failed to remove adviser'); }
  };

  // ── Intake pool (items B/E) — owner picks who's eligible + the pool's own
  // duration/interval, for unallocated Back Book clients with no adviser to scope a
  // personal link to. Reuses the same rotation-mode primitive as the company page
  // above, just a separate booking_pages row (page_type='intake'). ──────────────
  const loadIntakePage = () => {
    if (!isBrokerOwner) return;
    setIntakePageLoading(true);
    Promise.all([
      fetch(`${API}/broker/booking-page?type=intake`, { headers: { Authorization: `Bearer ${token()}` } }).then(r => r.ok ? r.json() : { page: null, advisers: [] }),
      fetch(`${API}/broker/booking-page/eligible-advisers`, { headers: { Authorization: `Bearer ${token()}` } }).then(r => r.ok ? r.json() : []).catch(() => []),
    ]).then(([bp, team]) => {
      setIntakePage(bp.page);
      setIntakeAdvisers(bp.advisers || []);
      setEligibleAdvisers(Array.isArray(team) ? team : []);
      if (bp.page) {
        setIntakeSlugInput(bp.page.slug);
        setIntakeDurationInput(bp.page.pool_duration_minutes || 30);
        setIntakeStepInput(bp.page.pool_availability_step || 15);
      }
    }).catch(() => setIntakePageError('Failed to load intake pool settings')).finally(() => setIntakePageLoading(false));
  };
  useEffect(() => { if (user && isBrokerOwner && navModules.backbook && navModules.scheduling) loadIntakePage(); }, [user, isBrokerOwner, navModules.backbook, navModules.scheduling]);

  const handleCreateIntakePage = async (e) => {
    e.preventDefault();
    setIntakeSaving(true); setIntakePageError(''); setIntakePageSuccess('');
    try {
      const res = await fetch(`${API}/broker/booking-page`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
        body: JSON.stringify({
          slug: intakeSlugInput.trim().toLowerCase(),
          pageType: 'intake',
          poolDurationMinutes: Number(intakeDurationInput),
          poolAvailabilityStep: Number(intakeStepInput),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) { setIntakePageSuccess('Intake pool page created.'); loadIntakePage(); }
      else setIntakePageError(data.error || 'Failed to create intake pool page');
    } catch { setIntakePageError('Failed to create intake pool page'); }
    setIntakeSaving(false);
  };

  const handleSaveIntakeSettings = async () => {
    if (!intakePage) return;
    setIntakeSaving(true); setIntakePageError(''); setIntakePageSuccess('');
    try {
      const res = await fetch(`${API}/broker/booking-page/${intakePage.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
        body: JSON.stringify({
          poolDurationMinutes: Number(intakeDurationInput),
          poolAvailabilityStep: Number(intakeStepInput),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) { setIntakePageSuccess('Pool duration/interval updated — synced to the live calendar profile.'); loadIntakePage(); }
      else setIntakePageError(data.error || 'Failed to save — the live calendar profile may not have updated');
    } catch { setIntakePageError('Failed to save intake pool settings'); }
    setIntakeSaving(false);
  };

  const handleAddIntakeAdviser = async () => {
    if (!intakeAddUserId || !intakePage) return;
    setIntakeSaving(true); setIntakePageError(''); setIntakePageSuccess('');
    try {
      const res = await fetch(`${API}/broker/booking-page/${intakePage.id}/advisers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
        body: JSON.stringify({ userId: intakeAddUserId }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) { setIntakeAddUserId(''); loadIntakePage(); }
      else setIntakePageError(data.error || 'Failed to add adviser to the pool');
    } catch { setIntakePageError('Failed to add adviser to the pool'); }
    setIntakeSaving(false);
  };

  const handleRemoveIntakeAdviser = async (adviserRowId) => {
    if (!window.confirm('Remove this adviser from the intake pool?')) return;
    try {
      const res = await fetch(`${API}/broker/booking-page/${intakePage.id}/advisers/${adviserRowId}`, {
        method: 'DELETE', headers: { Authorization: `Bearer ${token()}` },
      });
      if (res.ok) loadIntakePage(); else setIntakePageError('Failed to remove adviser from the pool');
    } catch { setIntakePageError('Failed to remove adviser from the pool'); }
  };


  // ── Load company profile (owner only) ────────────────────────────────────
  useEffect(() => {
    if (!user || !isBrokerOwner) return;
    fetch(`${API}/company-profile`, { headers: { Authorization: `Bearer ${token()}` } })
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (!d) return;
        const b = d.company.branding || {};
        setCompanyBio(b.company_bio || '');
        setCompanyLogoUrl(b.logo_url || '');
        setOfficePhotoUrl(b.office_photo_url || '');
        setLeadRouting(d.company.lead_routing || 'pool');
        const rc = d.company.branding?.routing_config || {};
        setPoolStrategy(rc.pool_strategy || 'first_to_claim');
        setClaimWindowMins(rc.claim_window_minutes || 30);
        setClaimTimeoutAction(rc.claim_timeout_action || 'admin');
        setWidgetEnabled(!!d.company.widget_enabled);
        setBrokerUserCount(typeof d.company.brokerUserCount === 'number' ? d.company.brokerUserCount : null);
      })
      .catch(() => {});
  }, [user, isBrokerOwner]);

  // ── Load commission settings (owner only) ────────────────────────────────
  useEffect(() => {
    if (!user || user.role !== 'owner') return;
    fetch(`${API}/commission-settings`, { headers: { Authorization: `Bearer ${token()}` } })
      .then(r => r.json())
      .then(d => {
        setNetworkFeePct(d.network_fee_percentage ?? 0);
        setCompAlloc(d.structure?.company_allocation ?? 100);
        setMgrAlloc(d.structure?.manager_allocation ?? 0);
        setBrkAlloc(d.structure?.broker_allocation ?? 0);
      })
      .catch(() => {});
  }, [user]);

  // ── Load preferences (owner only) ────────────────────────────────────────
  useEffect(() => {
    if (!user || !isBrokerOwner) return;
    fetch(`${API}/admin/company-settings`, { headers: { Authorization: `Bearer ${token()}` } })
      .then(r => r.json())
      .then(d => {
        setCompSettings(d);
        setDipExpiry(d.dip_expiry_days ?? 30);
        setNagDays(d.nag_days || [7, 14, 21]);
      })
      .catch(() => {});
  }, [user, isBrokerOwner]);

  // ── Load which modules this company has on ───────────────────────────────
  useEffect(() => {
    if (!user) return;
    fetch(`${API}/company-profile/nav-modules`, { headers: { Authorization: `Bearer ${token()}` } })
      .then(r => r.ok ? r.json() : { nav_modules: {} })
      .then(d => setNavModules(prev => ({ ...prev, ...d.nav_modules })))
      .catch(() => {});
  }, [user]);

  // ── Load Back Book preferences (owner only, only meaningful once we know backbook is on) ──
  useEffect(() => {
    if (!user || !isBrokerOwner || !navModules.backbook) return;
    fetch(`${API}/backbook/alert-schedule`, { headers: { Authorization: `Bearer ${token()}` } })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d?.months) setAlertMonths(d.months); })
      .catch(() => {});
  }, [user, isBrokerOwner, navModules.backbook]);

  useEffect(() => {
    if (!user || !navModules.backbook) return;
    fetch(`${API}/backbook/digest-preference`, { headers: { Authorization: `Bearer ${token()}` } })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setDigestEnabled(!!d.enabled); })
      .catch(() => {});
  }, [user, navModules.backbook]);

  // ── Handlers: My Profile ─────────────────────────────────────────────────
  const handleSaveMyProfile = async (e) => {
    e.preventDefault(); setProfileError(''); setProfileSuccess(''); setProfileSaving(true);
    try {
      const res = await fetch(`${API}/profile/bio`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
        body: JSON.stringify({ bio: myBio }),
      });
      const d = await res.json();
      if (!res.ok) setProfileError(d.error || 'Failed to save');
      else { setProfileSuccess('Profile saved.'); setTimeout(() => setProfileSuccess(''), 3000); }
    } catch { setProfileError('Network error.'); }
    finally { setProfileSaving(false); }
  };

  const handleProfilePhotoChange = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    setProfileError(''); setProfileSuccess('');
    const fd = new FormData(); fd.append('photo', file);
    try {
      const res = await fetch(`${API}/profile/upload-photo`, { method: 'POST', headers: { Authorization: `Bearer ${token()}` }, body: fd });
      const d = await res.json();
      if (!res.ok) setProfileError(d.error || 'Upload failed');
      else { setMyPhotoUrl(d.profile_photo_url); setProfileSuccess('Photo updated.'); setTimeout(() => setProfileSuccess(''), 3000); }
    } catch { setProfileError('Upload failed.'); }
  };

  // ── Handlers: Introducer entity (individual vs company) ──────────────────
  const handleSaveEntity = async (e) => {
    e.preventDefault(); setEntityError(''); setEntitySuccess('');
    if (entityType === 'company' && !entityCompanyName.trim()) {
      setEntityError('Please enter your company name, or switch to "Individual".');
      return;
    }
    setEntitySaving(true);
    try {
      const res = await fetch(`${API}/profile/introducer-entity`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
        body: JSON.stringify({ type: entityType, company_name: entityCompanyName, address: entityAddress }),
      });
      const d = await res.json();
      if (!res.ok) setEntityError(d.error || 'Failed to save');
      else { setEntitySuccess('Saved.'); setTimeout(() => setEntitySuccess(''), 3000); }
    } catch { setEntityError('Network error.'); }
    finally { setEntitySaving(false); }
  };

  // ── Handlers: Company Profile ─────────────────────────────────────────────
  const handleSaveCompanyProfile = async (e) => {
    e.preventDefault(); setCompProfError(''); setCompProfSuccess(''); setCompProfSaving(true);
    try {
      const res = await fetch(`${API}/company-profile/settings`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
        body: JSON.stringify({
            company_bio: companyBio,
            lead_routing: leadRouting,
            routing_config: leadRouting === 'pool' ? {
              pool_strategy: poolStrategy,
              claim_window_minutes: parseInt(claimWindowMins, 10),
              claim_timeout_action: claimTimeoutAction,
            } : null,
          }),
      });
      const d = await res.json();
      if (!res.ok) setCompProfError(d.error || 'Failed to save');
      else { setCompProfSuccess('Saved.'); setTimeout(() => setCompProfSuccess(''), 3000); }
    } catch { setCompProfError('Network error.'); }
    finally { setCompProfSaving(false); }
  };

  const handleLogoChange = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    const fd = new FormData(); fd.append('logo', file);
    try {
      const res = await fetch(`${API}/company-profile/upload-logo`, { method: 'POST', headers: { Authorization: `Bearer ${token()}` }, body: fd });
      const d = await res.json();
      if (!res.ok) setCompProfError(d.error || 'Upload failed');
      else { setCompanyLogoUrl(d.logo_url); setCompProfSuccess('Logo updated.'); setTimeout(() => setCompProfSuccess(''), 3000); }
    } catch { setCompProfError('Upload failed.'); }
  };

  const handleOfficePhotoChange = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    const fd = new FormData(); fd.append('photo', file);
    try {
      const res = await fetch(`${API}/company-profile/upload-office-photo`, { method: 'POST', headers: { Authorization: `Bearer ${token()}` }, body: fd });
      const d = await res.json();
      if (!res.ok) setCompProfError(d.error || 'Upload failed');
      else { setOfficePhotoUrl(d.office_photo_url); setCompProfSuccess('Photo updated.'); setTimeout(() => setCompProfSuccess(''), 3000); }
    } catch { setCompProfError('Upload failed.'); }
  };

  // ── Handlers: Commission ──────────────────────────────────────────────────
  const saveCommSettings = async () => {
    const total = Math.round((parseFloat(compAlloc||0)+parseFloat(mgrAlloc||0)+parseFloat(brkAlloc||0))*100)/100;
    if (total !== 100) { setCommError('Allocations must total 100%'); return; }
    setCommSaving(true); setCommError(''); setCommSuccess('');
    try {
      const r = await fetch(`${API}/commission-settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
        body: JSON.stringify({ network_fee_percentage: parseFloat(networkFeePct), company_allocation: parseFloat(compAlloc), manager_allocation: parseFloat(mgrAlloc), broker_allocation: parseFloat(brkAlloc) }),
      });
      const d = await r.json();
      if (!r.ok) { setCommError(d.error || 'Save failed'); return; }
      setCommSuccess('Saved.'); setTimeout(() => setCommSuccess(''), 3000);
    } catch { setCommError('Network error.'); }
    finally { setCommSaving(false); }
  };

  // ── Handlers: Password ───────────────────────────────────────────────────
  const handleChangePassword = async (e) => {
    e.preventDefault(); setPwError(''); setPwSuccess('');
    if (newPassword !== confirmPassword) { setPwError('Passwords do not match.'); return; }
    if (newPassword.length < 8) { setPwError('Must be at least 8 characters.'); return; }
    setPwLoading(true);
    try {
      const res = await fetch(`${API}/auth/change-password`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
        body: JSON.stringify({ currentPassword, newPassword }),
      });
      const d = await res.json();
      if (!res.ok) setPwError(d.error || 'Failed to update.');
      else { setPwSuccess('Password updated.'); setCurrentPassword(''); setNewPassword(''); setConfirmPassword(''); }
    } catch { setPwError('Network error.'); }
    finally { setPwLoading(false); }
  };

  // ── Handlers: Two-Factor Authentication ──────────────────────────────────
  const startTwoFaSetup = async () => {
    setTwoFaError(''); setTwoFaSuccess(''); setTwoFaLoading(true);
    try {
      const res = await fetch(`${API}/2fa/setup`, { method: 'POST', headers: { Authorization: `Bearer ${token()}` } });
      const d = await res.json();
      if (!res.ok) { setTwoFaError(d.error || 'Failed to start setup.'); return; }
      setTwoFaSecret(d);
      setTwoFaTotpInput(''); setTwoFaPhoneInput('');
      setTwoFaStep('qr');
    } catch { setTwoFaError('Network error.'); }
    finally { setTwoFaLoading(false); }
  };

  const confirmTwoFaSetup = async (e) => {
    e.preventDefault(); setTwoFaError(''); setTwoFaLoading(true);
    try {
      const res = await fetch(`${API}/2fa/verify-setup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
        body: JSON.stringify({ totpCode: twoFaTotpInput, phoneNumber: twoFaPhoneInput || undefined }),
      });
      const d = await res.json();
      if (!res.ok) { setTwoFaError(d.error || 'Invalid code.'); return; }
      setTwoFaBackupCodes(d.backupCodes || []);
      setTwoFaStep('backupCodes');
      loadTwoFaStatus();
    } catch { setTwoFaError('Network error.'); }
    finally { setTwoFaLoading(false); }
  };

  const finishTwoFaSetup = () => {
    setTwoFaStep('idle'); setTwoFaBackupCodes([]); setTwoFaSecret(null);
    setTwoFaSuccess('Two-factor authentication is now enabled.');
    setTimeout(() => setTwoFaSuccess(''), 4000);
  };

  const cancelTwoFaSetup = () => {
    setTwoFaStep('idle'); setTwoFaSecret(null); setTwoFaTotpInput(''); setTwoFaPhoneInput(''); setTwoFaError('');
  };

  const handleDisableTwoFa = async (e) => {
    e.preventDefault(); setTwoFaError(''); setTwoFaLoading(true);
    try {
      const res = await fetch(`${API}/2fa/disable`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
        body: JSON.stringify({ password: twoFaDisablePw }),
      });
      const d = await res.json();
      if (!res.ok) { setTwoFaError(d.error || 'Failed to disable.'); return; }
      setTwoFaDisablePw(''); setTwoFaStep('idle');
      setTwoFaSuccess('Two-factor authentication has been disabled.');
      setTimeout(() => setTwoFaSuccess(''), 4000);
      loadTwoFaStatus();
    } catch { setTwoFaError('Network error.'); }
    finally { setTwoFaLoading(false); }
  };

  // ── Handlers: Preferences ────────────────────────────────────────────────
  const addNagDay = () => {
    const v = parseInt(nagInput, 10);
    if (!v || v < 1 || v > 365 || nagDays.includes(v)) { setNagInput(''); return; }
    setNagDays([...nagDays, v].sort((a, b) => a - b)); setNagInput('');
  };
  const removeNagDay = (d) => setNagDays(nagDays.filter(x => x !== d));

  const handleSaveSettings = async (e) => {
    e.preventDefault(); setSettError(''); setSettSuccess('');
    if (!nagDays.length) { setSettError('Add at least one reminder day.'); return; }
    setSettLoading(true);
    try {
      const res = await fetch(`${API}/admin/company-settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
        body: JSON.stringify({ dip_expiry_days: parseInt(dipExpiry, 10), nag_days: nagDays }),
      });
      const d = await res.json();
      if (!res.ok) setSettError(d.error || 'Failed to save.');
      else { setCompSettings(d); setSettSuccess('Saved.'); setTimeout(() => setSettSuccess(''), 3000); }
    } catch { setSettError('Network error.'); }
    finally { setSettLoading(false); }
  };

  // ── Handlers: Back Book preferences ──────────────────────────────────────
  const addAlertMonth = () => {
    const v = parseInt(alertInput, 10);
    if (!v || v < 1 || v > 24 || alertMonths.includes(v)) { setAlertInput(''); return; }
    setAlertMonths([...alertMonths, v].sort((a, b) => b - a)); setAlertInput('');
  };
  const removeAlertMonth = (m) => setAlertMonths(alertMonths.filter(x => x !== m));

  const handleSaveAlertSchedule = async (e) => {
    e.preventDefault(); setBbPrefError(''); setBbPrefSuccess('');
    if (!alertMonths.length) { setBbPrefError('Add at least one trigger point.'); return; }
    setBbPrefLoading(true);
    try {
      const res = await fetch(`${API}/backbook/alert-schedule`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
        body: JSON.stringify({ months: alertMonths }),
      });
      const d = await res.json();
      if (!res.ok) setBbPrefError(d.error || 'Failed to save.');
      else { setAlertMonths(d.months); setBbPrefSuccess('Saved.'); setTimeout(() => setBbPrefSuccess(''), 3000); }
    } catch { setBbPrefError('Network error.'); }
    finally { setBbPrefLoading(false); }
  };

  const handleToggleDigest = async (next) => {
    setDigestEnabled(next); // optimistic — this is a single checkbox, not a form
    try {
      const res = await fetch(`${API}/backbook/digest-preference`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
        body: JSON.stringify({ enabled: next }),
      });
      if (!res.ok) setDigestEnabled(!next); // revert on failure
    } catch { setDigestEnabled(!next); }
  };

  const roleLabel  = (r) => r ? r.replace(/_/g,' ').replace(/\b\w/g,c=>c.toUpperCase()) : '---';
  const levelLabel = (l) => l ? l.charAt(0).toUpperCase()+l.slice(1) : null;

  // Whether the Preferences tab has anything to show at all — DIP section needs
  // Deals on, Back Book section needs Back Book on; if a company has neither (e.g.
  // clients-only), there's nothing left to put on this tab.
  const hasPrefsContent = navModules.deals !== false || !!navModules.backbook;

  const TABS = [
    { key: 'account', label: 'My Account' },
    ...(navModules.scheduling ? [{ key: 'calendar', label: 'Calendar & Booking' }] : []),
    ...(isBrokerOwner ? [{ key: 'company',  label: 'Company'      }] : []),
    ...(isBrokerOwner && hasPrefsContent ? [{ key: 'prefs', label: 'Preferences' }] : []),
    ...(isBrokerOwner ? [{ key: 'config',   label: 'Configuration'}] : []),
  ];

  return (
    <div style={{ display:'flex', minHeight:'100vh', background:'#F7FAFC' }}>
      <Sidebar />
      <main className="main-content" style={{ padding: mobile ? '70px 16px 32px' : '32px 40px', maxWidth: 760 }}>

        <div style={{ marginBottom:24 }}>
          <h1 style={{ fontSize:24, fontWeight:700, color:'#0e1b2c', margin:0 }}>Settings</h1>
        </div>

        {/* Tab bar */}
        <div style={{ display:'flex', borderBottom:'2px solid #EDF2F7', marginBottom:28, overflowX:'auto', WebkitOverflowScrolling:'touch' }}>
          {TABS.map(tab => (
            <button key={tab.key} onClick={() => setActiveTab(tab.key)} style={{
              background:'none', border:'none', padding:'10px 20px', fontSize:14, cursor:'pointer',
              fontWeight: activeTab===tab.key ? 700 : 500,
              color: activeTab===tab.key ? '#0e1b2c' : '#718096',
              borderBottom: activeTab===tab.key ? '3px solid #10b981' : '3px solid transparent',
              marginBottom:-2,
            }}>{tab.label}</button>
          ))}
        </div>

        {/* ── MY ACCOUNT TAB ── */}
        {activeTab === 'account' && <>
          <section style={cardStyle}>
            <h2 style={sectionTitle}>Account Details</h2>
            <div style={{ display:'grid', gridTemplateColumns: mobile ? '1fr' : '1fr 1fr', gap:'16px 32px', marginTop:16 }}>
              <Field label="Name"  value={user?.name  || '---'} />
              <Field label="Email" value={user?.email || '---'} />
              <Field label="Role"  value={roleLabel(user?.role)} />
              {user?.brokerLevel     && <Field label="Level"            value={levelLabel(user.brokerLevel)} />}
              {user?.introducerLevel && <Field label="Introducer Level" value={levelLabel(user.introducerLevel)} />}
            </div>
            <p style={{ marginTop:16, fontSize:12, color:'#a0aec0' }}>To update your name or email, contact your platform administrator.</p>
          </section>

          <section style={{ ...cardStyle, marginTop:24 }}>
            <h2 style={sectionTitle}>My Profile</h2>
            <p style={{ color:'#718096', fontSize:13, margin:'8px 0 20px' }}>
              {user?.role === 'introducer' ? 'Your photo and bio help your broker team know who you are.' : "Your photo and bio are shown to introducers on your broker's profile page."}
            </p>
            <form onSubmit={handleSaveMyProfile}>
              <div style={{ display:'flex', alignItems:'center', gap:20, marginBottom:24 }}>
                <div style={{ width:80, height:80, borderRadius:'50%', overflow:'hidden', background:'#EDF2F7', border:'2px solid #E2E8F0', flexShrink:0, display:'flex', alignItems:'center', justifyContent:'center', fontSize:28, color:'#a0aec0' }}>
                  {myPhotoUrl ? <img src={myPhotoUrl} alt="Profile" style={{ width:'100%', height:'100%', objectFit:'cover' }} /> : '👤'}
                </div>
                <div>
                  <button type="button" onClick={() => profilePhotoRef.current?.click()} style={secondaryBtn}>{myPhotoUrl ? 'Change photo' : 'Upload photo'}</button>
                  <p style={{ fontSize:11, color:'#a0aec0', margin:'4px 0 0' }}>JPEG, PNG or WebP · max 5 MB</p>
                  <input ref={profilePhotoRef} type="file" accept="image/jpeg,image/png,image/webp" style={{ display:'none' }} onChange={handleProfilePhotoChange} />
                </div>
              </div>
              <div style={{ marginBottom:16 }}>
                <label style={labelStyle}>Short bio</label>
                <p style={{ fontSize:12, color:'#a0aec0', margin:'2px 0 8px' }}>Max 500 characters.</p>
                <textarea value={myBio} onChange={e => setMyBio(e.target.value)} maxLength={500} rows={3}
                  style={{ ...inputStyle, width:'100%', resize:'vertical', boxSizing:'border-box' }}
                  placeholder="e.g. I specialise in first-time buyer mortgages with over 10 years' experience." />
                <p style={{ fontSize:11, color:'#a0aec0', margin:'4px 0 0', textAlign:'right' }}>{myBio.length}/500</p>
              </div>
              {profileError   && <div style={alertStyle('error')}>{profileError}</div>}
              {profileSuccess && <div style={alertStyle('success')}>{profileSuccess}</div>}
              <button type="submit" disabled={profileSaving} style={btnStyle(profileSaving)}>{profileSaving ? 'Saving...' : 'Save profile'}</button>
            </form>
          </section>

          {user?.role === 'introducer' && ['solo', 'company'].includes(user?.introducerLevel) && (
            <section style={{ ...cardStyle, marginTop:24 }}>
              <h2 style={sectionTitle}>Introducing As</h2>
              <p style={{ color:'#718096', fontSize:13, margin:'8px 0 20px' }}>
                Are you referring clients as an individual, or on behalf of a company? This is shown to
                your clients when they're asked to confirm their enquiry, and to your broker.
              </p>
              <form onSubmit={handleSaveEntity}>
                <div style={{ display:'flex', gap:10, marginBottom:16, maxWidth:400 }}>
                  <label style={{ flex:1, display:'flex', alignItems:'center', padding:'10px 14px', borderRadius:8, border:'1px solid #E2E8F0', fontSize:14, color:'#374151', cursor:'pointer', ...(entityType === 'individual' ? { borderColor:'#10b981', background:'#ecfdf5', color:'#065f46', fontWeight:600 } : {}) }}>
                    <input type="radio" name="entityType" checked={entityType === 'individual'} onChange={() => setEntityType('individual')} style={{ marginRight:8 }} />
                    Individual
                  </label>
                  <label style={{ flex:1, display:'flex', alignItems:'center', padding:'10px 14px', borderRadius:8, border:'1px solid #E2E8F0', fontSize:14, color:'#374151', cursor:'pointer', ...(entityType === 'company' ? { borderColor:'#10b981', background:'#ecfdf5', color:'#065f46', fontWeight:600 } : {}) }}>
                    <input type="radio" name="entityType" checked={entityType === 'company'} onChange={() => setEntityType('company')} style={{ marginRight:8 }} />
                    Company
                  </label>
                </div>
                {entityType === 'company' && (
                  <>
                    <div style={{ marginBottom:16, maxWidth:400 }}>
                      <label style={labelStyle}>Company Name</label>
                      <input type="text" value={entityCompanyName} onChange={e => setEntityCompanyName(e.target.value)}
                        placeholder="e.g. Smith Estate Agents Ltd" style={{ ...inputStyle, width:'100%', boxSizing:'border-box' }} />
                    </div>
                    <div style={{ marginBottom:16, maxWidth:400 }}>
                      <label style={labelStyle}>Company Address <span style={{ fontWeight:400, color:'#a0aec0' }}>(optional)</span></label>
                      <textarea value={entityAddress} onChange={e => setEntityAddress(e.target.value)} rows={3}
                        placeholder="Business or correspondence address" style={{ ...inputStyle, width:'100%', resize:'vertical', boxSizing:'border-box' }} />
                    </div>
                  </>
                )}
                {entityError   && <div style={alertStyle('error')}>{entityError}</div>}
                {entitySuccess && <div style={alertStyle('success')}>{entitySuccess}</div>}
                <button type="submit" disabled={entitySaving} style={btnStyle(entitySaving)}>{entitySaving ? 'Saving...' : 'Save'}</button>
              </form>
            </section>
          )}

          <section style={{ ...cardStyle, marginTop:24 }}>
            <h2 style={sectionTitle}>Change Password</h2>
            <form onSubmit={handleChangePassword} style={{ marginTop:16, maxWidth:400 }}>
              <FormField label="Current Password"     type="password" value={currentPassword} onChange={e=>setCurrentPassword(e.target.value)} autoComplete="current-password" />
              <FormField label="New Password"         type="password" value={newPassword}     onChange={e=>setNewPassword(e.target.value)}     autoComplete="new-password" hint="Minimum 8 characters" />
              <FormField label="Confirm New Password" type="password" value={confirmPassword} onChange={e=>setConfirmPassword(e.target.value)} autoComplete="new-password" />
              {pwError   && <div style={alertStyle('error')}>{pwError}</div>}
              {pwSuccess && <div style={alertStyle('success')}>{pwSuccess}</div>}
              <button type="submit" disabled={pwLoading} style={btnStyle(pwLoading)}>{pwLoading ? 'Updating...' : 'Update Password'}</button>
            </form>
          </section>

          <section style={{ ...cardStyle, marginTop:24 }}>
            <h2 style={sectionTitle}>Two-Factor Authentication</h2>

            {require2FABanner && (
              <div style={{ background:'#fef3c7', border:'1px solid #f59e0b', borderRadius:8, padding:'10px 14px', marginBottom:16, display:'flex', alignItems:'center', gap:10 }}>
                <span>🔒</span>
                <div>
                  <strong style={{ color:'#92400e' }}>Your organisation requires 2FA</strong>
                  <p style={{ margin:'2px 0 0', color:'#92400e', fontSize:13 }}>Please set up two-factor authentication before continuing to use the app.</p>
                </div>
              </div>
            )}

            {twoFaSuccess && <div style={alertStyle('success')}>{twoFaSuccess}</div>}

            {twoFaStep === 'idle' && (
              twoFaStatus?.enabled ? (
                <div style={{ marginTop:16 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:12 }}>
                    <span style={{ display:'inline-flex', alignItems:'center', padding:'4px 10px', borderRadius:20, background:'#d1fae5', border:'1px solid #6ee7b7', color:'#065f46', fontSize:13, fontWeight:600 }}>Enabled</span>
                    {twoFaStatus.setupDate && <span style={{ fontSize:12, color:'#a0aec0' }}>since {new Date(twoFaStatus.setupDate).toLocaleDateString()}</span>}
                  </div>
                  <p style={{ fontSize:13, color:'#718096', margin:'0 0 16px' }}>
                    Sign-in requires a code from your authenticator app{twoFaStatus.hasPhone ? ', with SMS available as a backup if you lose access to it' : ''}.
                  </p>
                  <button type="button" onClick={() => { setTwoFaStep('disable'); setTwoFaError(''); }} style={secondaryBtn}>Disable 2FA</button>
                </div>
              ) : (
                <div style={{ marginTop:16 }}>
                  <p style={{ fontSize:13, color:'#718096', margin:'0 0 16px' }}>
                    Add an extra layer of security to your account. Once enabled, signing in will require a code from
                    an authenticator app (Google Authenticator, Authy, 1Password, Microsoft Authenticator, etc.) in addition to your password.
                  </p>
                  {twoFaError && <div style={alertStyle('error')}>{twoFaError}</div>}
                  <button type="button" onClick={startTwoFaSetup} disabled={twoFaLoading} style={btnStyle(twoFaLoading)}>
                    {twoFaLoading ? 'Starting...' : 'Enable Two-Factor Authentication'}
                  </button>
                </div>
              )
            )}

            {twoFaStep === 'qr' && twoFaSecret && (
              <form onSubmit={confirmTwoFaSetup} style={{ marginTop:16 }}>
                <p style={{ fontSize:13, color:'#718096', margin:'0 0 16px' }}>
                  Scan this QR code with an authenticator app, or enter the key manually.
                </p>
                <div style={{ display:'flex', gap:24, flexWrap:'wrap', alignItems:'flex-start', marginBottom:20 }}>
                  <img src={twoFaSecret.qrCode} alt="2FA QR code" style={{ width:180, height:180, border:'1px solid #E2E8F0', borderRadius:8 }} />
                  <div style={{ flex:1, minWidth:220 }}>
                    <label style={labelStyle}>Manual entry key</label>
                    <div style={{ ...inputStyle, marginTop:6, fontFamily:'monospace', fontSize:13, wordBreak:'break-all', userSelect:'all' }}>{twoFaSecret.manualEntry}</div>

                    <div style={{ marginTop:20 }}>
                      <label style={labelStyle}>Phone number <span style={{ fontWeight:400, color:'#a0aec0' }}>(optional — SMS backup if you lose your authenticator)</span></label>
                      <input type="tel" value={twoFaPhoneInput} onChange={e => setTwoFaPhoneInput(e.target.value)} placeholder="+44 7700 900123"
                        style={{ ...inputStyle, width:'100%', marginTop:6, boxSizing:'border-box' }} />
                    </div>

                    <div style={{ marginTop:20 }}>
                      <label style={labelStyle}>Enter the 6-digit code from your app to confirm</label>
                      <input type="text" inputMode="numeric" autoComplete="one-time-code" value={twoFaTotpInput}
                        onChange={e => setTwoFaTotpInput(e.target.value)} maxLength={6} placeholder="123456" required
                        style={{ ...inputStyle, width:140, marginTop:6, fontSize:18, letterSpacing:'0.15em', textAlign:'center' }} />
                    </div>
                  </div>
                </div>
                {twoFaError && <div style={alertStyle('error')}>{twoFaError}</div>}
                <div style={{ display:'flex', gap:10 }}>
                  <button type="submit" disabled={twoFaLoading} style={btnStyle(twoFaLoading)}>{twoFaLoading ? 'Verifying...' : 'Confirm & enable'}</button>
                  <button type="button" onClick={cancelTwoFaSetup} style={secondaryBtn}>Cancel</button>
                </div>
              </form>
            )}

            {twoFaStep === 'backupCodes' && (
              <div style={{ marginTop:16 }}>
                <div style={alertStyle('success')}>Two-factor authentication is enabled.</div>
                <p style={{ fontSize:13, color:'#718096', margin:'12px 0' }}>
                  Save these backup codes somewhere safe. Each one can be used once to sign in if you lose access to
                  your authenticator app. They won't be shown again.
                </p>
                <div style={{ display:'grid', gridTemplateColumns: mobile ? '1fr 1fr' : 'repeat(5, 1fr)', gap:8, background:'#F7FAFC', border:'1px solid #E2E8F0', borderRadius:8, padding:16, marginBottom:16 }}>
                  {twoFaBackupCodes.map(c => (
                    <div key={c} style={{ fontFamily:'monospace', fontSize:13, color:'#0e1b2c', textAlign:'center' }}>{c}</div>
                  ))}
                </div>
                <button type="button" onClick={finishTwoFaSetup} style={btnStyle(false)}>I've saved these codes</button>
              </div>
            )}

            {twoFaStep === 'disable' && (
              <form onSubmit={handleDisableTwoFa} style={{ marginTop:16, maxWidth:400 }}>
                <p style={{ fontSize:13, color:'#718096', margin:'0 0 16px' }}>Confirm your password to disable two-factor authentication.</p>
                <FormField label="Password" type="password" value={twoFaDisablePw} onChange={e => setTwoFaDisablePw(e.target.value)} autoComplete="current-password" />
                {twoFaError && <div style={alertStyle('error')}>{twoFaError}</div>}
                <div style={{ display:'flex', gap:10 }}>
                  <button type="submit" disabled={twoFaLoading} style={btnStyle(twoFaLoading)}>{twoFaLoading ? 'Disabling...' : 'Disable 2FA'}</button>
                  <button type="button" onClick={() => { setTwoFaStep('idle'); setTwoFaDisablePw(''); setTwoFaError(''); }} style={secondaryBtn}>Cancel</button>
                </div>
              </form>
            )}
          </section>

        </>}

        {/* ── CALENDAR & BOOKING TAB ── */}
        {/* Own dedicated tab (19 Aug 2026) — previously the Calendar Sync/My Booking
            Hours sections lived inside My Account, and company-wide booking page setup
            was a fully separate routed page (/booking-setup, own Sidebar/layout). Both
            folded in here so calendar/booking configuration lives in one place. */}
        {activeTab === 'calendar' && <>
          {calendarAvailable && (
            <section style={cardStyle}>
              <h2 style={sectionTitle}>Calendar Sync</h2>
              {calendarBannerMsg && <div style={alertStyle(calendarParam === 'error' ? 'error' : 'success')}>{calendarBannerMsg}</div>}
              {calendarError && <div style={alertStyle('error')}>{calendarError}</div>}
              {calendarConnection ? (
                <div style={{ marginTop:16 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:12 }}>
                    <span style={{ display:'inline-flex', alignItems:'center', padding:'4px 10px', borderRadius:20, background:'#d1fae5', border:'1px solid #6ee7b7', color:'#065f46', fontSize:13, fontWeight:600 }}>
                      Connected — {calendarConnection.provider}
                    </span>
                    {calendarConnection.aurinko_account_email && <span style={{ fontSize:12, color:'#a0aec0' }}>{calendarConnection.aurinko_account_email}</span>}
                  </div>
                  <p style={{ fontSize:13, color:'#718096', margin:'0 0 16px' }}>
                    Your real availability from this calendar powers the team calendar view and client booking.
                  </p>
                  <button type="button" onClick={handleDisconnectCalendar} style={secondaryBtn}>Disconnect</button>
                </div>
              ) : (
                <div style={{ marginTop:16 }}>
                  <p style={{ fontSize:13, color:'#718096', margin:'0 0 16px' }}>
                    Connect your calendar so your real availability shows up on the team diary view and the public booking page.
                  </p>
                  <div style={{ display:'flex', gap:10 }}>
                    <button type="button" onClick={() => handleConnectCalendar('Google')} disabled={calendarConnecting} style={btnStyle(calendarConnecting)}>
                      {calendarConnecting ? 'Connecting...' : 'Connect Google Calendar'}
                    </button>
                    <button type="button" onClick={() => handleConnectCalendar('Office365')} disabled={calendarConnecting} style={secondaryBtn}>
                      Connect Outlook / Office 365
                    </button>
                  </div>
                </div>
              )}
            </section>
          )}

          {calendarAvailable && calendarConnection && (
            <section style={{ ...cardStyle, marginTop:24 }}>
              <h2 style={sectionTitle}>My Booking Hours</h2>
              <p style={{ fontSize:13, color:'#718096', margin:'8px 0 20px' }}>
                Set your own work hours, meeting length and slot interval for the public booking page. Only you can see or edit these — not your manager or owner.
              </p>
              {bookingProfileError && <div style={alertStyle('error')}>{bookingProfileError}</div>}
              {bookingProfileSuccess && <div style={alertStyle('success')}>{bookingProfileSuccess}</div>}
              {bookingProfileLoading && !bookingProfile && <p style={{ color:'#a0aec0', fontSize:13 }}>Loading...</p>}
              {bookingProfile && (
                <form onSubmit={handleSaveBookingHours}>
                  <div style={{ display:'flex', gap:24, marginBottom:20, flexWrap:'wrap' }}>
                    <div>
                      <label style={labelStyle}>Meeting duration (minutes)</label>
                      <div><input type="number" min="10" max="240" step="5" value={bpDuration} onChange={e=>setBpDuration(e.target.value)} style={{ ...inputStyle, width:120, marginTop:8 }} /></div>
                    </div>
                    <div>
                      <label style={labelStyle}>Slot interval (minutes)</label>
                      <p style={{ fontSize:11, color:'#a0aec0', margin:'2px 0 0', maxWidth:200 }}>How often a new bookable time is offered — e.g. every 15 min, regardless of meeting length.</p>
                      {/* Was a free-typed number input (min 5/max 120/step 5), which let
                          Tim submit 45 and hit a real "Failed to save booking hours" bug
                          (19 Aug 2026) — Aurinko's own validator only accepts this fixed
                          set, confirmed live against the API, not documented anywhere.
                          A dropdown makes the invalid state unreachable from the UI. */}
                      <div>
                        <select value={bpAvailabilityStep} onChange={e=>setBpAvailabilityStep(e.target.value)} style={{ ...inputStyle, width:120, marginTop:8 }}>
                          {[5, 10, 15, 20, 30, 60].map(v => <option key={v} value={v}>{v}</option>)}
                        </select>
                      </div>
                    </div>
                  </div>
                  <div style={{ marginBottom:20 }}>
                    <label style={labelStyle}>Working hours</label>
                    {['monday','tuesday','wednesday','thursday','friday','saturday','sunday'].map(day => (
                      <div key={day} style={{ display:'flex', alignItems:'center', gap:12, marginTop:10 }}>
                        <label style={{ display:'flex', alignItems:'center', gap:6, width:110, cursor:'pointer', textTransform:'capitalize', fontSize:13 }}>
                          <input type="checkbox" checked={!!bpDaySchedules[day]?.enabled}
                            onChange={e => setBpDaySchedules({ ...bpDaySchedules, [day]: { ...bpDaySchedules[day], enabled: e.target.checked } })} />
                          {day}
                        </label>
                        <input type="time" disabled={!bpDaySchedules[day]?.enabled} value={bpDaySchedules[day]?.start || '09:00'}
                          onChange={e => setBpDaySchedules({ ...bpDaySchedules, [day]: { ...bpDaySchedules[day], start: e.target.value } })}
                          style={{ ...inputStyle, width:110 }} />
                        <span style={{ fontSize:13, color:'#a0aec0' }}>to</span>
                        <input type="time" disabled={!bpDaySchedules[day]?.enabled} value={bpDaySchedules[day]?.end || '17:00'}
                          onChange={e => setBpDaySchedules({ ...bpDaySchedules, [day]: { ...bpDaySchedules[day], end: e.target.value } })}
                          style={{ ...inputStyle, width:110 }} />
                      </div>
                    ))}
                  </div>
                  <button type="submit" disabled={bookingProfileLoading} style={btnStyle(bookingProfileLoading)}>{bookingProfileLoading ? 'Saving...' : 'Save Booking Hours'}</button>
                </form>
              )}
            </section>
          )}

          {calendarAvailable && calendarConnection && (
            <section style={{ ...cardStyle, marginTop:24 }}>
              <h2 style={sectionTitle}>My Personal Booking Link</h2>
              <p style={{ fontSize:13, color:'#718096', margin:'8px 0 20px' }}>
                A booking link scoped to just you — for a client who already knows they're
                working with you, so they land straight on your availability with no
                "choose an adviser" step. Uses the same hours/duration/interval above.
              </p>
              {personalLinkError && <div style={alertStyle('error')}>{personalLinkError}</div>}
              {personalLink ? (
                <p style={{ fontSize:13, margin:0 }}>
                  <a href={`${PUBLIC_BOOKING_BASE}/${personalLink.slug}`} target="_blank" rel="noreferrer">{PUBLIC_BOOKING_BASE}/{personalLink.slug}</a>
                </p>
              ) : (
                <button type="button" onClick={handleCreatePersonalLink} disabled={personalLinkLoading} style={btnStyle(personalLinkLoading)}>
                  {personalLinkLoading ? 'Creating...' : 'Create My Personal Link'}
                </button>
              )}
            </section>
          )}

          {isBrokerOwner && navModules.scheduling && (
            <section style={{ ...cardStyle, marginTop:24 }}>
              {/* Merged into one card at Tim's request (19 Aug 2026) — the named/
                  personal company booking page and the Back Book intake pool are both
                  "company diary" settings and were sitting as two separate cards with
                  no coherent grouping. Pool duration/interval fold directly in below,
                  not their own visual sub-card — just a divider between the two
                  concerns within the same box. */}
              <h2 style={sectionTitle}>Company Diary Settings</h2>
              <p style={{ fontSize:13, color:'#718096', margin:'8px 0 20px' }}>
                Your company's shared booking pages — clients book time directly via
                Aurinko's calendar availability. Each adviser sets their own hours above.
              </p>

              <div style={{ fontSize:14, fontWeight:700, color:'#0e1b2c', marginBottom:4 }}>Company Booking Page</div>
              <p style={{ fontSize:12, color:'#a0aec0', margin:'0 0 16px' }}>
                The public page clients use to book time — named mode lets them pick
                an adviser, rotation mode books whoever's free.
              </p>
              {companyPageError && <div style={alertStyle('error')}>{companyPageError}</div>}
              {companyPageSuccess && <div style={alertStyle('success')}>{companyPageSuccess}</div>}
              {companyPageLoading && <p style={{ color:'#a0aec0', fontSize:13 }}>Loading...</p>}

              {!companyPageLoading && !companyPage && (
                <form onSubmit={handleCreateCompanyPage}>
                  <div style={{ marginBottom:16 }}>
                    <label style={labelStyle}>Page URL slug</label>
                    <p style={{ fontSize:12, color:'#a0aec0', margin:'2px 0 8px' }}>Your page will live at {PUBLIC_BOOKING_BASE}/&lt;slug&gt;</p>
                    <input value={companySlugInput} onChange={e => setCompanySlugInput(e.target.value)} placeholder="e.g. yourcompany" style={{ ...inputStyle, width:'100%', maxWidth:320 }} />
                  </div>
                  <div style={{ marginBottom:16 }}>
                    <label style={labelStyle}>How should advisers be selected?</label>
                    <div style={{ display:'flex', gap:16, marginTop:8 }}>
                      <label style={{ display:'flex', alignItems:'center', gap:6, cursor:'pointer' }}>
                        <input type="radio" checked={companyModeInput === 'named'} onChange={() => setCompanyModeInput('named')} />
                        <span style={{ fontSize:13 }}>Client picks a named adviser</span>
                      </label>
                      <label style={{ display:'flex', alignItems:'center', gap:6, cursor:'pointer' }}>
                        <input type="radio" checked={companyModeInput === 'simple_rotation'} onChange={() => setCompanyModeInput('simple_rotation')} />
                        <span style={{ fontSize:13 }}>Book whoever's free (rotation)</span>
                      </label>
                    </div>
                  </div>
                  <button type="submit" disabled={companySaving || !companySlugInput} style={btnStyle(companySaving)}>{companySaving ? 'Creating...' : 'Create Booking Page'}</button>
                </form>
              )}

              {!companyPageLoading && companyPage && (
                <>
                  <p style={{ fontSize:13, margin:'4px 0 4px' }}>
                    <a href={`${PUBLIC_BOOKING_BASE}/${companyPage.slug}`} target="_blank" rel="noreferrer">{PUBLIC_BOOKING_BASE}/{companyPage.slug}</a>
                  </p>
                  <p style={{ fontSize:12, color:'#a0aec0', margin:'0 0 16px' }}>
                    Mode: {companyPage.adviser_selection_mode === 'named' ? 'Client picks a named adviser' : "Book whoever's free (rotation)"}
                  </p>
                  <div style={{ borderTop:'1px solid #EDF2F7', paddingTop:16 }}>
                    <div style={{ fontSize:13, fontWeight:700, color:'#0e1b2c', marginBottom:10 }}>Advisers on this page</div>
                    {companyAdvisers.length === 0 && <p style={{ fontSize:13, color:'#a0aec0' }}>No advisers added yet.</p>}
                    {companyAdvisers.map(a => (
                      <div key={a.id} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'8px 0', borderBottom:'1px solid #EDF2F7' }}>
                        <div>
                          <div style={{ fontSize:14, fontWeight:600 }}>{a.user_name}</div>
                          <div style={{ fontSize:12, color: a.has_calendar ? '#065f46' : '#991b1b' }}>{a.has_calendar ? 'Calendar connected' : 'No calendar connected — will not appear live'}</div>
                        </div>
                        <button onClick={() => handleRemoveCompanyAdviser(a.id)} style={secondaryBtn}>Remove</button>
                      </div>
                    ))}
                    <div style={{ display:'flex', gap:10, marginTop:14 }}>
                      <select value={companyAddUserId} onChange={e => setCompanyAddUserId(e.target.value)} style={{ ...inputStyle, flex:1 }}>
                        <option value="">Add an adviser...</option>
                        {eligibleAdvisers.filter(m => !companyAdvisers.some(a => a.user_id === m.id)).map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                      </select>
                      <button onClick={handleAddCompanyAdviser} disabled={!companyAddUserId || companySaving} style={btnStyle(companySaving)}>Add</button>
                    </div>
                  </div>
                </>
              )}

              {navModules.backbook && (
                <div style={{ borderTop:'1px solid #EDF2F7', marginTop:28, paddingTop:24 }}>
                  <div style={{ fontSize:14, fontWeight:700, color:'#0e1b2c', marginBottom:4 }}>Back Book Intake Pool</div>
                  <p style={{ fontSize:12, color:'#a0aec0', margin:'0 0 16px' }}>
                    For Back Book clients who don't yet belong to any adviser. Pick which
                    advisers are eligible to pick up a booking from this pool — could be 2
                    people or the whole team — and set the pool's own meeting length/interval
                    (rotation mode has no single adviser whose own hours apply).
                  </p>
                  {intakePageError && <div style={alertStyle('error')}>{intakePageError}</div>}
                  {intakePageSuccess && <div style={alertStyle('success')}>{intakePageSuccess}</div>}
                  {intakePageLoading && <p style={{ color:'#a0aec0', fontSize:13 }}>Loading...</p>}

                  {!intakePageLoading && !intakePage && (
                    <form onSubmit={handleCreateIntakePage}>
                      <div style={{ marginBottom:16 }}>
                        <label style={labelStyle}>Page URL slug</label>
                        <p style={{ fontSize:12, color:'#a0aec0', margin:'2px 0 8px' }}>Your intake page will live at {PUBLIC_BOOKING_BASE}/&lt;slug&gt;</p>
                        <input value={intakeSlugInput} onChange={e => setIntakeSlugInput(e.target.value)} placeholder="e.g. yourcompany-intake" style={{ ...inputStyle, width:'100%', maxWidth:320 }} />
                      </div>
                      <div style={{ display:'flex', gap:24, marginBottom:16, flexWrap:'wrap' }}>
                        <div>
                          <label style={labelStyle}>Pool meeting duration (minutes)</label>
                          <input type="number" min="10" max="240" step="5" value={intakeDurationInput} onChange={e => setIntakeDurationInput(e.target.value)} style={{ ...inputStyle, width:120, marginTop:8 }} />
                        </div>
                        <div>
                          <label style={labelStyle}>Pool slot interval (minutes)</label>
                          <select value={intakeStepInput} onChange={e => setIntakeStepInput(e.target.value)} style={{ ...inputStyle, width:120, marginTop:8 }}>
                            {[5, 10, 15, 20, 30, 60].map(v => <option key={v} value={v}>{v}</option>)}
                          </select>
                        </div>
                      </div>
                      <button type="submit" disabled={intakeSaving || !intakeSlugInput} style={btnStyle(intakeSaving)}>{intakeSaving ? 'Creating...' : 'Create Intake Pool Page'}</button>
                    </form>
                  )}

                  {!intakePageLoading && intakePage && (
                    <>
                      <p style={{ fontSize:13, margin:'4px 0 16px' }}>
                        <a href={`${PUBLIC_BOOKING_BASE}/${intakePage.slug}`} target="_blank" rel="noreferrer">{PUBLIC_BOOKING_BASE}/{intakePage.slug}</a>
                      </p>
                      <div style={{ display:'flex', gap:24, marginBottom:16, flexWrap:'wrap', alignItems:'flex-end' }}>
                        <div>
                          <label style={labelStyle}>Pool meeting duration (minutes)</label>
                          <input type="number" min="10" max="240" step="5" value={intakeDurationInput} onChange={e => setIntakeDurationInput(e.target.value)} style={{ ...inputStyle, width:120, marginTop:8 }} />
                        </div>
                        <div>
                          <label style={labelStyle}>Pool slot interval (minutes)</label>
                          <select value={intakeStepInput} onChange={e => setIntakeStepInput(e.target.value)} style={{ ...inputStyle, width:120, marginTop:8 }}>
                            {[5, 10, 15, 20, 30, 60].map(v => <option key={v} value={v}>{v}</option>)}
                          </select>
                        </div>
                        <button type="button" onClick={handleSaveIntakeSettings} disabled={intakeSaving} style={btnStyle(intakeSaving)}>{intakeSaving ? 'Saving...' : 'Save & Sync to Calendar'}</button>
                      </div>
                      <div style={{ borderTop:'1px solid #EDF2F7', paddingTop:16 }}>
                        <div style={{ fontSize:13, fontWeight:700, color:'#0e1b2c', marginBottom:10 }}>Advisers in this pool</div>
                        {intakeAdvisers.length === 0 && <p style={{ fontSize:13, color:'#a0aec0' }}>No advisers in the pool yet.</p>}
                        {intakeAdvisers.map(a => (
                          <div key={a.id} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'8px 0', borderBottom:'1px solid #EDF2F7' }}>
                            <div>
                              <div style={{ fontSize:14, fontWeight:600 }}>{a.user_name}</div>
                              <div style={{ fontSize:12, color: a.has_calendar ? '#065f46' : '#991b1b' }}>{a.has_calendar ? 'Calendar connected' : 'No calendar connected — will not rotate live'}</div>
                            </div>
                            <button onClick={() => handleRemoveIntakeAdviser(a.id)} style={secondaryBtn}>Remove</button>
                          </div>
                        ))}
                        <div style={{ display:'flex', gap:10, marginTop:14 }}>
                          <select value={intakeAddUserId} onChange={e => setIntakeAddUserId(e.target.value)} style={{ ...inputStyle, flex:1 }}>
                            <option value="">Add an adviser to the pool...</option>
                            {eligibleAdvisers.filter(m => !intakeAdvisers.some(a => a.user_id === m.id)).map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                          </select>
                          <button onClick={handleAddIntakeAdviser} disabled={!intakeAddUserId || intakeSaving} style={btnStyle(intakeSaving)}>Add</button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}
            </section>
          )}
        </>}

        {/* ── COMPANY TAB (owner only) ── */}

        {activeTab === 'company' && isBrokerOwner && <>
          <section style={cardStyle}>
            <h2 style={sectionTitle}>Company Profile</h2>
            <p style={{ color:'#718096', fontSize:13, margin:'8px 0 20px' }}>Your logo, photo and bio appear on the introducer "About Broker" page and lead submission form.</p>
            <form onSubmit={handleSaveCompanyProfile}>
              <div style={{ marginBottom:24 }}>
                <label style={labelStyle}>Company logo</label>
                <div style={{ display:'flex', alignItems:'center', gap:16, marginTop:10 }}>
                  <div style={{ width:100, height:60, borderRadius:8, overflow:'hidden', background:'#F7FAFC', border:'1px solid #E2E8F0', display:'flex', alignItems:'center', justifyContent:'center', fontSize:12, color:'#a0aec0' }}>
                    {companyLogoUrl ? <img src={companyLogoUrl} alt="Logo" style={{ maxWidth:'100%', maxHeight:'100%', objectFit:'contain' }} /> : 'No logo'}
                  </div>
                  <div>
                    <button type="button" onClick={() => logoRef.current?.click()} style={secondaryBtn}>{companyLogoUrl ? 'Change logo' : 'Upload logo'}</button>
                    <p style={{ fontSize:11, color:'#a0aec0', margin:'4px 0 0' }}>JPEG, PNG or WebP · max 8 MB</p>
                    <input ref={logoRef} type="file" accept="image/jpeg,image/png,image/webp" style={{ display:'none' }} onChange={handleLogoChange} />
                  </div>
                </div>
              </div>
              <div style={{ marginBottom:24 }}>
                <label style={labelStyle}>Office / team photo</label>
                <div style={{ display:'flex', alignItems:'center', gap:16, marginTop:10 }}>
                  <div style={{ width:140, height:80, borderRadius:8, overflow:'hidden', background:'#F7FAFC', border:'1px solid #E2E8F0', display:'flex', alignItems:'center', justifyContent:'center', fontSize:12, color:'#a0aec0' }}>
                    {officePhotoUrl ? <img src={officePhotoUrl} alt="Office" style={{ width:'100%', height:'100%', objectFit:'cover' }} /> : 'No photo'}
                  </div>
                  <div>
                    <button type="button" onClick={() => officePhotoRef.current?.click()} style={secondaryBtn}>{officePhotoUrl ? 'Change photo' : 'Upload photo'}</button>
                    <p style={{ fontSize:11, color:'#a0aec0', margin:'4px 0 0' }}>JPEG, PNG or WebP · max 8 MB · landscape recommended</p>
                    <input ref={officePhotoRef} type="file" accept="image/jpeg,image/png,image/webp" style={{ display:'none' }} onChange={handleOfficePhotoChange} />
                  </div>
                </div>
              </div>
              <div style={{ marginBottom:24 }}>
                <label style={labelStyle}>Company bio</label>
                <p style={{ fontSize:12, color:'#a0aec0', margin:'2px 0 8px' }}>Shown on the broker profile page. Max 800 characters.</p>
                <textarea value={companyBio} onChange={e => setCompanyBio(e.target.value)} maxLength={800} rows={4}
                  style={{ ...inputStyle, width:'100%', resize:'vertical', boxSizing:'border-box' }}
                  placeholder="e.g. We're an independent mortgage broker based in Manchester, helping clients since 2010." />
                <p style={{ fontSize:11, color:'#a0aec0', margin:'4px 0 0', textAlign:'right' }}>{companyBio.length}/800</p>
              </div>
              {navModules.leads !== false && (
              <div style={{ marginBottom:24 }}>
                <label style={labelStyle}>Lead routing</label>
                <p style={{ fontSize:12, color:'#a0aec0', margin:'2px 0 12px' }}>How inbound leads from introducers and your website are assigned to your team.</p>

                {brokerUserCount !== null && brokerUserCount <= 1 ? (
                  <div style={{ display:'flex', gap:12, alignItems:'flex-start', padding:'14px 16px', border:'2px solid #E2E8F0', borderRadius:8, background:'#F7FAFC' }}>
                    <div style={{ fontSize:20 }}>🎯</div>
                    <div>
                      <div style={{ fontSize:14, fontWeight:700, color:'#0e1b2c' }}>Direct assignment</div>
                      <div style={{ fontSize:12, color:'#718096', marginTop:3, lineHeight:1.5 }}>Leads go straight to you. Shared pool distribution (round robin, first-to-claim, admin queue) unlocks automatically once you add another broker to your team.</div>
                    </div>
                  </div>
                ) : (
                  <>
                  {/* Top-level: Direct vs Pool */}
                  <div style={{ display:'flex', gap:12, flexWrap:'wrap', marginBottom: leadRouting === 'pool' ? 0 : 0 }}>
                    {[
                      { value:'direct', label:'Direct assignment',  icon:'🎯', desc:'Leads go straight to the broker linked to the introducer. No queue, no delay.' },
                      { value:'pool',   label:'Shared pool',         icon:'🔄', desc:'All leads enter a shared pool and are distributed across your team.' },
                    ].map(opt => (
                      <label key={opt.value} style={{ display:'flex', gap:12, alignItems:'flex-start', padding:'14px 16px', border:`2px solid ${leadRouting===opt.value?'#10b981':'#E2E8F0'}`, borderRadius:8, cursor:'pointer', flex:1, minWidth:200, background: leadRouting===opt.value?'#f0fdf8':'#fff', transition:'all 0.15s' }}>
                        <input type="radio" value={opt.value} checked={leadRouting===opt.value} onChange={() => setLeadRouting(opt.value)} style={{ marginTop:3, accentColor:'#10b981', flexShrink:0 }} />
                        <div>
                          <div style={{ fontSize:14, fontWeight:700, color:'#0e1b2c' }}>{opt.icon} {opt.label}</div>
                          <div style={{ fontSize:12, color:'#718096', marginTop:3, lineHeight:1.5 }}>{opt.desc}</div>
                        </div>
                      </label>
                    ))}
                  </div>

                  {/* Pool strategy — revealed when Shared Pool is selected */}
                  {leadRouting === 'pool' && (
                    <div style={{ marginTop:16, padding:'20px', background:'#F7FAFC', border:'1px solid #E2E8F0', borderRadius:8 }}>
                      <div style={{ fontSize:13, fontWeight:700, color:'#4b5a6e', marginBottom:14, textTransform:'uppercase', letterSpacing:'0.04em' }}>Pool Routing Strategy</div>

                      <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                        {[
                          { value:'first_to_claim', label:'First to Claim', icon:'⚡',
                            desc:"All eligible brokers are notified simultaneously. First to click 'Claim' gets the lead. Creates urgency and rewards brokers who are switched on." },
                          { value:'round_robin',    label:'Round Robin',    icon:'🔁',
                            desc:'Leads are distributed evenly in rotation: broker A gets lead 1, broker B gets lead 2, and so on. Fair and predictable.' },
                          { value:'admin_assigns',  label:'Admin Assigns',  icon:'👤',
                            desc:'Every lead waits in a queue. An admin is notified and manually picks who it goes to. Maximum control, ideal for complex cases.' },
                        ].map(opt => (
                          <label key={opt.value} style={{ display:'flex', gap:12, alignItems:'flex-start', padding:'13px 15px', border:`2px solid ${poolStrategy===opt.value?'#6366f1':'#E2E8F0'}`, borderRadius:8, cursor:'pointer', background: poolStrategy===opt.value?'#eef2ff':'#fff', transition:'all 0.15s' }}>
                            <input type="radio" value={opt.value} checked={poolStrategy===opt.value} onChange={() => setPoolStrategy(opt.value)} style={{ marginTop:3, accentColor:'#6366f1', flexShrink:0 }} />
                            <div>
                              <div style={{ fontSize:14, fontWeight:700, color:'#0e1b2c' }}>{opt.icon} {opt.label}</div>
                              <div style={{ fontSize:12, color:'#718096', marginTop:3, lineHeight:1.5 }}>{opt.desc}</div>
                            </div>
                          </label>
                        ))}
                      </div>

                      {/* First to Claim sub-options */}
                      {poolStrategy === 'first_to_claim' && (
                        <div style={{ marginTop:16, padding:'16px', background:'#fff', border:'1px solid #c7d2fe', borderRadius:8, borderLeft:'3px solid #6366f1' }}>
                          <div style={{ fontSize:12, fontWeight:700, color:'#6366f1', marginBottom:14, textTransform:'uppercase', letterSpacing:'0.04em' }}>Claim Window &amp; Timeout</div>

                          <div style={{ marginBottom:16 }}>
                            <label style={{ display:'block', fontSize:13, fontWeight:600, color:'#4b5a6e', marginBottom:8 }}>
                              How long do brokers have to claim a lead before it times out?
                            </label>
                            <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                              {[
                                { v: 15,  label: '15 min' },
                                { v: 30,  label: '30 min' },
                                { v: 60,  label: '1 hour' },
                                { v: 120, label: '2 hours' },
                                { v: 240, label: '4 hours' },
                              ].map(opt => (
                                <button key={opt.v} type="button"
                                  onClick={() => setClaimWindowMins(opt.v)}
                                  style={{ padding:'7px 16px', borderRadius:20, fontSize:13, fontWeight:600, cursor:'pointer', border:`2px solid ${claimWindowMins===opt.v?'#6366f1':'#E2E8F0'}`, background: claimWindowMins===opt.v?'#6366f1':'#fff', color: claimWindowMins===opt.v?'#fff':'#4b5a6e', transition:'all 0.12s' }}>
                                  {opt.label}
                                </button>
                              ))}
                            </div>
                          </div>

                          <div>
                            <label style={{ display:'block', fontSize:13, fontWeight:600, color:'#4b5a6e', marginBottom:8 }}>
                              If nobody claims within that window, what happens?
                            </label>
                            <div style={{ display:'flex', gap:10, flexWrap:'wrap' }}>
                              {[
                                { value:'admin',       label:'Notify admin to assign manually', icon:'📋',
                                  desc:'The lead drops into an admin queue with a notification. Good if you want a safety net on unclaimed leads.' },
                                { value:'round_robin', label:'Auto-assign via round robin',      icon:'🔁',
                                  desc:"The next broker in the rotation gets it automatically. Zero admin overhead — leads never sit unclaimed." },
                              ].map(opt => (
                                <label key={opt.value} style={{ display:'flex', gap:10, alignItems:'flex-start', padding:'11px 13px', border:`2px solid ${claimTimeoutAction===opt.value?'#f59e0b':'#E2E8F0'}`, borderRadius:8, cursor:'pointer', flex:1, minWidth:180, background: claimTimeoutAction===opt.value?'#fffbeb':'#fff', transition:'all 0.15s' }}>
                                  <input type="radio" value={opt.value} checked={claimTimeoutAction===opt.value} onChange={() => setClaimTimeoutAction(opt.value)} style={{ marginTop:3, accentColor:'#f59e0b', flexShrink:0 }} />
                                  <div>
                                    <div style={{ fontSize:13, fontWeight:700, color:'#0e1b2c' }}>{opt.icon} {opt.label}</div>
                                    <div style={{ fontSize:12, color:'#718096', marginTop:3, lineHeight:1.5 }}>{opt.desc}</div>
                                  </div>
                                </label>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  </>
                )}
              </div>
              )}
              {compProfError   && <div style={alertStyle('error')}>{compProfError}</div>}
              {compProfSuccess && <div style={alertStyle('success')}>{compProfSuccess}</div>}
              <button type="submit" disabled={compProfSaving} style={btnStyle(compProfSaving)}>{compProfSaving ? 'Saving...' : 'Save company profile'}</button>
            </form>
          </section>

          {navModules.deals !== false && (
          <section style={{ ...cardStyle, marginTop:24 }}>
            <h2 style={sectionTitle}>Commission Settings</h2>
            <p style={{ fontSize:13, color:'#718096', marginTop:12, marginBottom:20 }}>
              Set your network fee deduction and how net case value is split internally. The fee is deducted from the lender proc fee before any commission is calculated.
            </p>
            <div style={{ marginBottom:24 }}>
              <h3 style={{ fontSize:14, fontWeight:700, color:'#0e1b2c', margin:'0 0 12px' }}>Network Fee</h3>
              <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                <div style={{ position:'relative', width:140 }}>
                  <input type="number" min="0" max="100" step="0.1" value={networkFeePct} onChange={e=>setNetworkFeePct(e.target.value)}
                    style={{ ...inputStyle, width:'100%', paddingRight:28, boxSizing:'border-box' }} />
                  <span style={{ position:'absolute', right:10, top:'50%', transform:'translateY(-50%)', color:'#718096', fontSize:13 }}>%</span>
                </div>
                <span style={{ fontSize:13, color:'#718096' }}>of lender proc fee, deducted before any split</span>
              </div>
              {networkFeePct > 0 && <p style={{ fontSize:12, color:'#718096', marginTop:6 }}>Example: £1,000 proc fee → £{(1000 - 1000*parseFloat(networkFeePct||0)/100).toFixed(2)} net</p>}
            </div>
            <div style={{ marginBottom:20 }}>
              <h3 style={{ fontSize:14, fontWeight:700, color:'#0e1b2c', margin:'0 0 4px' }}>Internal Split</h3>
              <p style={{ fontSize:12, color:'#718096', margin:'0 0 14px' }}>How net case value is divided. Must total 100%.</p>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:16 }}>
                {[{ label:'Company',value:compAlloc,set:setCompAlloc },{ label:'Manager',value:mgrAlloc,set:setMgrAlloc },{ label:'Broker',value:brkAlloc,set:setBrkAlloc }].map(({label,value,set}) => (
                  <div key={label}>
                    <label style={{ ...labelStyle, marginBottom:6, display:'block' }}>{label}</label>
                    <div style={{ position:'relative' }}>
                      <input type="number" min="0" max="100" step="0.5" value={value} onChange={e=>set(e.target.value)}
                        style={{ ...inputStyle, width:'100%', paddingRight:28, boxSizing:'border-box' }} />
                      <span style={{ position:'absolute', right:10, top:'50%', transform:'translateY(-50%)', color:'#718096', fontSize:13 }}>%</span>
                    </div>
                  </div>
                ))}
              </div>
              {(()=>{ const t=Math.round((parseFloat(compAlloc||0)+parseFloat(mgrAlloc||0)+parseFloat(brkAlloc||0))*100)/100; const ok=t===100; return <p style={{ marginTop:8, fontSize:13, fontWeight:600, color:ok?'#065f46':'#991b1b' }}>Total: {t}% {ok?'✓':'— must equal 100%'}</p>; })()}
            </div>
            {commError   && <div style={alertStyle('error')}>{commError}</div>}
            {commSuccess && <div style={alertStyle('success')}>{commSuccess}</div>}
            <button onClick={saveCommSettings} disabled={commSaving} style={btnStyle(commSaving)}>{commSaving ? 'Saving...' : 'Save Commission Settings'}</button>
          </section>
          )}
        </>}

        {/* ── PREFERENCES TAB (owner only) ── */}
        {activeTab === 'prefs' && isBrokerOwner && <>
          {navModules.deals !== false && (
          <section style={{ ...cardStyle, marginBottom: navModules.backbook ? 20 : 0 }}>
            <h2 style={sectionTitle}>DIP & Document Reminders</h2>
            <p style={{ color:'#718096', fontSize:13, margin:'8px 0 20px' }}>Configure DIP certificate expiry and document reminder schedule for your company.</p>
            <form onSubmit={handleSaveSettings}>
              <div style={{ marginBottom:24 }}>
                <label style={labelStyle}>DIP Certificate Expiry (days)</label>
                <p style={{ fontSize:12, color:'#a0aec0', margin:'2px 0 8px' }}>How long a DIP certificate download link remains active for introducers.</p>
                <input type="number" min="1" max="365" value={dipExpiry} onChange={e=>setDipExpiry(e.target.value)} style={{ ...inputStyle, width:120 }} />
              </div>
              <div style={{ marginBottom:24 }}>
                <label style={labelStyle}>Document Reminder Schedule</label>
                <p style={{ fontSize:12, color:'#a0aec0', margin:'2px 0 8px' }}>Days after the upload invite is sent to remind clients with outstanding documents. Default: 7, 14, 21.</p>
                <div style={{ display:'flex', flexWrap:'wrap', gap:8, marginBottom:12 }}>
                  {nagDays.map(d=>(
                    <span key={d} style={chipStyle}>Day {d}
                      <button type="button" onClick={()=>removeNagDay(d)} style={{ background:'none',border:'none',cursor:'pointer',color:'#4b5a6e',fontSize:14,padding:'0 0 0 6px',lineHeight:1 }}>×</button>
                    </span>
                  ))}
                  {nagDays.length===0 && <span style={{ fontSize:13,color:'#a0aec0',fontStyle:'italic' }}>No reminders set</span>}
                </div>
                <div style={{ display:'flex', gap:8, alignItems:'center' }}>
                  <input type="number" min="1" max="365" placeholder="e.g. 7" value={nagInput} onChange={e=>setNagInput(e.target.value)}
                    onKeyDown={e=>e.key==='Enter'&&(e.preventDefault(),addNagDay())} style={{ ...inputStyle, width:100 }} />
                  <button type="button" onClick={addNagDay} style={{ padding:'8px 16px',background:'#EDF2F7',color:'#4b5a6e',border:'1px solid #E2E8F0',borderRadius:6,fontSize:13,fontWeight:600,cursor:'pointer' }}>+ Add day</button>
                </div>
              </div>
              {settError   && <div style={alertStyle('error')}>{settError}</div>}
              {settSuccess && <div style={alertStyle('success')}>{settSuccess}</div>}
              <button type="submit" disabled={settLoading} style={btnStyle(settLoading)}>{settLoading ? 'Saving...' : 'Save Settings'}</button>
            </form>
          </section>
          )}

          {navModules.backbook && (
          <section style={cardStyle}>
            <h2 style={sectionTitle}>Back Book Preferences</h2>
            <p style={{ color:'#718096', fontSize:13, margin:'8px 0 20px' }}>Configure renewal alerts and the monthly director digest for your back book.</p>
            <form onSubmit={handleSaveAlertSchedule}>
              <div style={{ marginBottom:24 }}>
                <label style={labelStyle}>Renewal Alert Schedule</label>
                <p style={{ fontSize:12, color:'#a0aec0', margin:'2px 0 8px' }}>How many months before a product's end date to email the client. Default: 8, 6, 3 — add a closer trigger point (e.g. 1 month) if you want a final urgent nudge.</p>
                <div style={{ display:'flex', flexWrap:'wrap', gap:8, marginBottom:12 }}>
                  {alertMonths.map(m=>(
                    <span key={m} style={chipStyle}>{m} {m===1?'month':'months'} before
                      <button type="button" onClick={()=>removeAlertMonth(m)} style={{ background:'none',border:'none',cursor:'pointer',color:'#4b5a6e',fontSize:14,padding:'0 0 0 6px',lineHeight:1 }}>×</button>
                    </span>
                  ))}
                  {alertMonths.length===0 && <span style={{ fontSize:13,color:'#a0aec0',fontStyle:'italic' }}>No alerts set — clients won't be chased at all</span>}
                </div>
                <div style={{ display:'flex', gap:8, alignItems:'center' }}>
                  <input type="number" min="1" max="24" placeholder="e.g. 1" value={alertInput} onChange={e=>setAlertInput(e.target.value)}
                    onKeyDown={e=>e.key==='Enter'&&(e.preventDefault(),addAlertMonth())} style={{ ...inputStyle, width:100 }} />
                  <button type="button" onClick={addAlertMonth} style={{ padding:'8px 16px',background:'#EDF2F7',color:'#4b5a6e',border:'1px solid #E2E8F0',borderRadius:6,fontSize:13,fontWeight:600,cursor:'pointer' }}>+ Add trigger</button>
                </div>
              </div>
              {bbPrefError   && <div style={alertStyle('error')}>{bbPrefError}</div>}
              {bbPrefSuccess && <div style={alertStyle('success')}>{bbPrefSuccess}</div>}
              <button type="submit" disabled={bbPrefLoading} style={btnStyle(bbPrefLoading)}>{bbPrefLoading ? 'Saving...' : 'Save Alert Schedule'}</button>
            </form>

            <div style={{ marginTop:28, paddingTop:24, borderTop:'1px solid #EDF2F7' }}>
              <label style={{ display:'flex', alignItems:'center', gap:10, cursor:'pointer' }}>
                <input type="checkbox" checked={digestEnabled} onChange={e=>handleToggleDigest(e.target.checked)}
                  style={{ width:16, height:16, cursor:'pointer' }} />
                <span>
                  <span style={{ fontSize:13, fontWeight:600, color:'#4b5a6e', display:'block' }}>Monthly director digest</span>
                  <span style={{ fontSize:12, color:'#a0aec0' }}>Get a monthly email summary of the whole back book — upcoming renewals and recent outcomes across every adviser, not just your own clients.</span>
                </span>
              </label>
            </div>
          </section>
          )}
        </>}

        {/* ── CONFIGURATION TAB (owner only) ── */}
        {activeTab === 'config' && isBrokerOwner && <>
          <p style={{ fontSize:14, color:'#718096', marginBottom:24 }}>Manage your workflow configuration. These settings apply across your whole company.</p>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(210px, 1fr))', gap:16 }}>
            {[
              ...(navModules.introducers !== false ? [{ icon:'📋', label:'Agreements',      desc:'View introducer agreement templates approved for your company', route:'/agreements-status' }] : []),
              ...(navModules.deals !== false ? [{ icon:'📁', label:'Document Types',  desc:'Configure document types requested from clients',          route:'/admin/document-types' }] : []),
              { icon:'✉️', label:'Email Templates', desc: navModules.backbook && navModules.deals === false ? 'Customise your Back Book renewal reminder email' : 'Customise automated emails sent to introducers and clients', route:'/admin/email-templates' },
              ...(navModules.deals !== false ? [{ icon:'🔀', label:'Workflow Stages', desc:'Define deal pipeline stages for each mortgage product type', route:'/stages' }] : []),
              ...(widgetEnabled && navModules.leads !== false ? [{ icon:'🌐', label:'Web Widget', desc:'Manage your website lead capture widget and embed code', route:'/admin/widget' }] : []),
              ...(navModules.scheduling ? [{ icon:'🗓️', label:'Booking Page', desc:'Set up your public booking page and choose which advisers appear on it', tab:'calendar' }] : []),
            ].map(item => (
              <button key={item.label} onClick={() => item.tab ? setActiveTab(item.tab) : navigate(item.route)}
                style={{ background:'#fff', border:'1px solid #E2E8F0', borderRadius:10, padding:'20px', textAlign:'left', cursor:'pointer', transition:'box-shadow 0.15s' }}
                onMouseEnter={e => e.currentTarget.style.boxShadow='0 4px 12px rgba(0,0,0,0.08)'}
                onMouseLeave={e => e.currentTarget.style.boxShadow='none'}>
                <div style={{ fontSize:28, marginBottom:10 }}>{item.icon}</div>
                <div style={{ fontSize:14, fontWeight:700, color:'#0e1b2c', marginBottom:6 }}>{item.label}</div>
                <div style={{ fontSize:12, color:'#718096', lineHeight:1.5 }}>{item.desc}</div>
                <div style={{ marginTop:14, fontSize:12, color:'#10b981', fontWeight:600 }}>Manage →</div>
              </button>
            ))}
          </div>
        </>}

      </main>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div style={{ fontSize:11, fontWeight:600, color:'#a0aec0', textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:4 }}>{label}</div>
      <div style={{ fontSize:15, color:'#0e1b2c', fontWeight:500 }}>{value}</div>
    </div>
  );
}

function FormField({ label, type, value, onChange, autoComplete, hint }) {
  return (
    <div style={{ marginBottom:16 }}>
      <label style={{ display:'block', fontSize:13, fontWeight:600, color:'#4b5a6e', marginBottom:6 }}>{label}</label>
      <input type={type} value={value} onChange={onChange} required autoComplete={autoComplete}
        style={{ width:'100%', padding:'9px 12px', border:'1px solid #E2E8F0', borderRadius:6, fontSize:14, color:'#0e1b2c', outline:'none', boxSizing:'border-box', background:'#fff' }} />
      {hint && <p style={{ margin:'4px 0 0', fontSize:12, color:'#a0aec0' }}>{hint}</p>}
    </div>
  );
}

const secondaryBtn = { padding:'8px 16px', background:'#fff', color:'#4b5a6e', border:'1px solid #CBD5E0', borderRadius:6, fontSize:13, fontWeight:600, cursor:'pointer' };
const cardStyle    = { background:'#fff', border:'1px solid #E2E8F0', borderRadius:10, padding:'24px 28px' };
const sectionTitle = { fontSize:16, fontWeight:700, color:'#0e1b2c', margin:0, paddingBottom:12, borderBottom:'1px solid #EDF2F7' };
const labelStyle   = { display:'block', fontSize:13, fontWeight:600, color:'#4b5a6e' };
const inputStyle   = { padding:'8px 12px', border:'1px solid #E2E8F0', borderRadius:6, fontSize:14, color:'#0e1b2c', outline:'none', background:'#fff' };
const chipStyle    = { display:'inline-flex', alignItems:'center', padding:'4px 10px', borderRadius:20, background:'#EBF8FF', border:'1px solid #BEE3F8', color:'#2b6cb0', fontSize:13, fontWeight:600 };
const btnStyle     = (loading) => ({ marginTop:8, padding:'10px 24px', background: loading?'#a0aec0':'#10b981', color:'#fff', border:'none', borderRadius:6, fontWeight:600, fontSize:14, cursor: loading?'not-allowed':'pointer' });
const alertStyle   = (type) => ({ margin:'12px 0', padding:'10px 14px', borderRadius:6, fontSize:13, fontWeight:500, background: type==='error'?'#fee2e2':'#d1fae5', color: type==='error'?'#991b1b':'#065f46', border:`1px solid ${type==='error'?'#fca5a5':'#6ee7b7'}` });
